/* eslint-disable no-underscore-dangle -- AbstractMentionResource names its notify methods with an underscore */
import { AbstractMentionResource } from '@atlaskit/mention/abstract-mention-resource';
import { type ResolvingMentionProvider } from '@atlaskit/mention/mention-resource';
import {
  type MentionContextIdentifier,
  type MentionDescription,
  type MentionStats,
} from '@atlaskit/mention/types';

// AbstractMentionResource sends listeners at most 20 mentions.
const MAX_MENTIONS = 20;

// The typeahead's agents section shows at most 5.
export const MAX_AGENT_MENTIONS = 5;

/**
 * An agent the host offers. Matches `ConfluenceAgentMention` in `@canvas/frame-api-types`.
 */
export interface AgentMention {
  id: string;
  name?: string;
  mentionName?: string;
  avatarUrl?: string;
  description?: string;
}

export type GetAgentMentions = (
  query: string,
  maxResults: number,
) => Promise<AgentMention[]>;

// Keys must be unique across providers, since they all subscribe to the same people provider.
let nextPeopleKey = 0;

const toAgentMention = ({
  id,
  name,
  mentionName,
  avatarUrl,
  description,
}: AgentMention): MentionDescription => ({
  id,
  name,
  mentionName,
  avatarUrl,
  description,
  // The editor keeps `userType` on the mention node. Confluence reads it to spot the agent.
  userType: 'APP',
  appType: 'agent',
});

const mergeMentions = (
  people: MentionDescription[],
  agents: MentionDescription[],
): MentionDescription[] => {
  if (agents.length === 0) {
    return people;
  }

  const agentIds = new Set(agents.map(({ id }) => id));
  const otherPeople = people.filter(({ id }) => !agentIds.has(id));

  // Leave room for the agents under the cap. The typeahead puts them in their own section.
  return [...otherPeople.slice(0, MAX_MENTIONS - agents.length), ...agents];
};

/**
 * Wraps the people provider and adds agents from the host. People show as soon as
 * they load. Agents are added in a second update when the host answers. If the
 * host fails, only people show.
 */
export class AgentMentionProvider
  extends AbstractMentionResource
  implements ResolvingMentionProvider
{
  private readonly people: ResolvingMentionProvider;
  private readonly getAgentMentions: GetAgentMentions;
  private filterCount = 0;
  private peopleKey: string | undefined;

  constructor(people: ResolvingMentionProvider, getAgentMentions: GetAgentMentions) {
    super();
    this.people = people;
    this.getAgentMentions = getAgentMentions;
  }

  filter(query = '', contextIdentifier?: MentionContextIdentifier) {
    const filterId = ++this.filterCount;
    const isLatest = () => filterId === this.filterCount;

    let people: MentionDescription[] | undefined;
    let peopleStats: MentionStats | undefined;
    let agents: MentionDescription[] = [];

    const notify = () => {
      if (!people || !isLatest()) {
        return;
      }

      const result = { query, mentions: mergeMentions(people, agents) };
      this._notifyListeners(result, peopleStats);
      this._notifyAllResultsListeners(result);
    };

    // Only the latest query matters, so stop listening for the previous one.
    this.stopListeningForPeople();
    const peopleKey = `agent-mention-provider-${++nextPeopleKey}`;
    this.peopleKey = peopleKey;

    this.people.subscribe(
      peopleKey,
      (mentions, resultQuery = '', stats) => {
        if (resultQuery !== query) {
          return;
        }

        this.stopListeningForPeople(peopleKey);
        people = mentions;
        peopleStats = stats;
        notify();
      },
      (error, errorQuery = '') => {
        if (errorQuery !== query) {
          return;
        }

        this.stopListeningForPeople(peopleKey);
        if (isLatest()) {
          this._notifyErrorListeners(error, errorQuery);
        }
      },
      (info) => this._notifyInfoListeners(info),
      undefined,
      (event, actionSubject, action, attributes) =>
        this._notifyAnalyticsListeners(event, actionSubject, action, attributes),
    );

    void this.loadAgents(query).then((loaded) => {
      // Without agents there's nothing to add to the people results.
      if (loaded.length === 0) {
        return;
      }

      agents = loaded;
      notify();
    });

    this.people.filter(query, contextIdentifier);
  }

  shouldHighlightMention(mention: MentionDescription) {
    return this.people.shouldHighlightMention(mention);
  }

  isFiltering(query: string) {
    return this.people.isFiltering(query);
  }

  // Canvas's own selected-mention state lives in the people provider.
  recordMentionSelection(
    mention: MentionDescription,
    contextIdentifier?: MentionContextIdentifier,
  ) {
    this.people.recordMentionSelection(mention, contextIdentifier);
  }

  cacheMentionName(id: string, mentionName: string) {
    this.people.cacheMentionName(id, mentionName);
  }

  resolveMentionName(id: string) {
    return this.people.resolveMentionName(id);
  }

  supportsMentionNameResolving() {
    return this.people.supportsMentionNameResolving();
  }

  private stopListeningForPeople(key = this.peopleKey) {
    if (key && key === this.peopleKey) {
      this.people.unsubscribe(key);
      this.peopleKey = undefined;
    }
  }

  private async loadAgents(query: string): Promise<MentionDescription[]> {
    try {
      const agents = await this.getAgentMentions(query, MAX_AGENT_MENTIONS);

      return Array.isArray(agents)
        ? agents.slice(0, MAX_AGENT_MENTIONS).map(toAgentMention)
        : [];
    } catch {
      // The host resolves [] when it fails, but a throw shouldn't hide people either.
      return [];
    }
  }
}

/**
 * Builds a mention provider that adds the host's agents to the people results.
 */
export const getAgentMentionProvider = async (
  peopleProvider: Promise<ResolvingMentionProvider>,
  getAgentMentions: GetAgentMentions,
) => new AgentMentionProvider(await peopleProvider, getAgentMentions);
